// Quality modes shown on cards and in the processing queue. These are a Valoi
// concept (not a provider switch) — every mode is rendered by Kling. The engine
// now sends the chosen mode on each item, so this no longer has to guess; what
// it does instead is translate the engine's vocabulary into the UI's.
import type { GenerationMetadata } from '@/shared/types';

/* The UI's names for the engine's modes. 'standard' is what the UI has always called
   the engine's `storytelling`; 'non-narrative' keeps its engine name because there is no
   older UI word for it. Nothing consumes this type today -- `modeLabel` takes a loose
   `string` -- but an exported union that is missing a real value is a trap for whoever
   reaches for it first. */
export type GenerationMode = 'cinematic' | 'standard' | 'non-narrative';

/**
 * The engine's `mode` as a label. It calls the non-cinematic mode
 * `storytelling`; the UI has always called it "Standard", and this is the one
 * place that translation lives.
 *
 * Still defaults an absent mode to "Standard" — this feeds badges that have to
 * render *something*. `describeGeneration` below is the caller that must not
 * guess, and it guards on presence itself.
 */
export function modeLabel(mode?: string): string {
  if (mode === 'cinematic') return 'Cinematic';
  // The characters speak their own lines. Needs naming here for the same reason a style
  // does: `describeGeneration` omits anything it cannot label, so a mode we ship a
  // control for would otherwise leave a silent gap in the meta row.
  if (mode === 'non-narrative') return 'Non-narrative';
  return 'Standard';
}

const STYLE_LABELS: Record<string, string> = {
  storybook: 'Storybook',
  photoreal: 'Photoreal',
  // Not offered by the prompt-box picker — the CLI submits it — but jobs made
  // that way show up in the same library. Labelling one "Storybook" because it
  // is not "Photoreal" is exactly the guess this row exists to avoid.
  documentation: 'Documentation',
};

/**
 * How a video was made, for the content page's meta row: style, quality mode
 * and aspect ratio, in that order, with anything unknown left out entirely.
 *
 * Only what the backend actually said. `modeLabel` defaults an absent mode to
 * Standard, which is correct for a badge that has to render *something* — but
 * wrong here, so this guards on presence first. The submission page this
 * replaces defaulted, and its badge said Standard for a year regardless of what
 * the engine did.
 *
 * Both fields now arrive from the engine (`jobs.mode` / `jobs.style`), so this
 * row is finally a fact about the video rather than about this browser session.
 * An unrecognised style is omitted rather than guessed.
 */
export function describeGeneration(metadata?: GenerationMetadata): string[] {
  const parts: string[] = [];
  const style = metadata?.style ? STYLE_LABELS[metadata.style] : undefined;
  if (style) parts.push(style);
  if (metadata?.mode) parts.push(modeLabel(metadata.mode));
  if (metadata?.aspectRatio) parts.push(metadata.aspectRatio);
  return parts;
}
